/**
 * Best-effort charset detection for user-uploaded CSVs.
 *
 * Uploaded metadata CSVs come from customers' spreadsheet tools, and Excel on a
 * German/Windows locale still exports "CSV" as Windows-1252 (ANSI) rather than
 * UTF-8. Blindly decoding those bytes as UTF-8 corrupts umlauts (ä/ö/ü/ß) before
 * the data ever leaves the browser. This resolves the encoding in three layers,
 * deterministic first, probabilistic last:
 *
 *   1. Byte-order mark        — definitive (Excel's "CSV UTF-8" writes one).
 *   2. UTF-8 self-validation  — definitive; UTF-8 is self-validating, and legacy
 *      8-bit umlaut text essentially never forms valid UTF-8 by accident.
 *   3. jschardet statistical guess, biased to Windows-1252 for our European
 *      customer base and decoded natively via TextDecoder. Only a high-confidence
 *      guess overrides the default; anything softer is returned `confident: false`
 *      so the caller can ask the user to confirm.
 *
 * TextDecoder decodes the whole WHATWG set (windows-1252, iso-8859-x,
 * windows-1251, shift_jis, gb18030, …) natively, so once we have a label there is
 * no need for an iconv-style dependency.
 */

const BOMS: ReadonlyArray<{ readonly signature: readonly number[]; readonly encoding: string }> = [
  { signature: [0xef, 0xbb, 0xbf], encoding: 'utf-8' },
  { signature: [0xff, 0xfe], encoding: 'utf-16le' },
  { signature: [0xfe, 0xff], encoding: 'utf-16be' }
]

/** jschardet confidence at or above this is trusted without a user confirm. */
const CONFIDENCE_THRESHOLD = 0.9

/** Cap on the detector input; sample-name CSVs are tiny, this just bounds big files. */
const DETECT_SAMPLE_BYTES = 64 * 1024

/** Default for non-UTF-8 files: superset of Latin-1, covers our EU customer base. */
const EU_FALLBACK = 'windows-1252'

export type DetectedEncoding = {
  /** WHATWG label suitable for TextDecoder / FileReader.readAsText. */
  encoding: string
  /** The file's bytes decoded with `encoding`. */
  text: string
  /** false when `encoding` is a low-confidence guess and should be confirmed by the user. */
  confident: boolean
}

function startsWithSignature(bytes: Uint8Array, signature: readonly number[]): boolean {
  if (bytes.length < signature.length) return false
  return signature.every((b, i) => bytes[i] === b)
}

function decodeWith(bytes: Uint8Array, label: string): { text: string; encoding: string } {
  try {
    return { text: new TextDecoder(label).decode(bytes), encoding: label }
  } catch {
    // Unknown/unsupported label (e.g. an exotic detector guess) — fall back to the EU default.
    return { text: new TextDecoder(EU_FALLBACK).decode(bytes), encoding: EU_FALLBACK }
  }
}

/** jschardet expects a "binary string" where each char code is a single byte. */
function toBinaryString(bytes: Uint8Array): string {
  let out = ''
  for (let i = 0; i < bytes.length; i++) out += String.fromCharCode(bytes[i])
  return out
}

function normalizeLabel(raw: string): string {
  const lower = raw.trim().toLowerCase()
  // Pure ASCII is a strict subset of UTF-8; decode it losslessly as UTF-8.
  if (lower === 'ascii' || lower === 'us-ascii') return 'utf-8'
  return lower
}

export async function detectEncoding(file: File): Promise<DetectedEncoding> {
  const bytes = new Uint8Array(await file.arrayBuffer())

  // 1. BOM — definitive.
  for (const { signature, encoding } of BOMS) {
    if (startsWithSignature(bytes, signature)) {
      return { ...decodeWith(bytes, encoding), confident: true }
    }
  }

  // 2. UTF-8 self-validation — definitive.
  try {
    const text = new TextDecoder('utf-8', { fatal: true }).decode(bytes)
    return { encoding: 'utf-8', text, confident: true }
  } catch {
    // Not valid UTF-8 — fall through to statistical detection.
  }

  // 3. jschardet, EU-biased. Lazy-imported so it stays out of the main bundle for
  //    the common UTF-8 path.
  let label = EU_FALLBACK
  let confident = false
  try {
    const { detect } = await import('jschardet')
    const sample = bytes.length > DETECT_SAMPLE_BYTES ? bytes.subarray(0, DETECT_SAMPLE_BYTES) : bytes
    const guess = detect(toBinaryString(sample))
    if (guess?.encoding && guess.confidence >= CONFIDENCE_THRESHOLD) {
      label = normalizeLabel(guess.encoding)
      confident = true
    }
  } catch {
    // Detector unavailable — keep the Windows-1252 default, unconfident.
  }

  return { ...decodeWith(bytes, label), confident }
}

const FRIENDLY_ENCODING_NAMES: Readonly<Record<string, string>> = {
  'utf-8': 'UTF-8',
  'utf-16le': 'UTF-16 (little-endian)',
  'utf-16be': 'UTF-16 (big-endian)',
  'windows-1252': 'Windows-1252 (Western European)',
  'windows-1251': 'Windows-1251 (Cyrillic)',
  'iso-8859-1': 'ISO-8859-1 (Latin-1)',
  'iso-8859-2': 'ISO-8859-2 (Central European)',
  'iso-8859-15': 'ISO-8859-15 (Latin-9)'
}

/** Human-readable label for a WHATWG encoding, for use in confirm dialogs. */
export function friendlyEncodingName(encoding: string): string {
  return FRIENDLY_ENCODING_NAMES[encoding] ?? encoding
}